import { z } from 'zod';
import { idSchema, nameSchema, urlSchema } from './common';
import { EVENT_TYPES } from './inbox';

/**
 * Monitoring dostępności i incydenty (MVP §14, Etap 9, §17 nr 7; MODULE_UTRZYMANIE §5; Fala 4). Tabele: `monitors`
 * (adres, warunki, progi, tryb live / symulacja, bieżący stan i liczniki), `monitor_checks` (wyniki sprawdzeń,
 * retencja 90 dni), `incidents` (jedna otwarta na monitor — unikalny indeks częściowy).
 *
 * Zasady: odpytywanie co 2 min, alarm po 2 kolejnych niepowodzeniach, poprawa potwierdzona po 2 sukcesach (parametry
 * edytowalne). Pojedynczy błąd nie tworzy alarmu ani serii push. Jedna awaria = jeden incydent; otwarcie → zdarzenie
 * `monitor.down` (Pilne, push), zamknięcie → `monitor.up`. Moduł uprawnień: Utrzymanie. Tryb `simulated` (dev,
 * konektory na końcu): wynik sprawdzenia bierze się z `simulated_state`, nic nie jest wysyłane do strony, wyniki
 * i incydenty mają oznaczenie symulacji.
 */

export const MONITOR_METHODS = ['GET', 'HEAD'] as const;
export type MonitorMethod = (typeof MONITOR_METHODS)[number];

/** `live` = prawdziwe żądanie HTTP; `simulated` = wynik z `simulated_state` (dev, przed podłączeniem konektorów). */
export const MONITOR_MODES = ['live', 'simulated'] as const;
export type MonitorMode = (typeof MONITOR_MODES)[number];

/** Stan, który zwraca symulacja (przełącznik dev „Symuluj awarię”). CHECK: tryb `simulated` ⇒ ustawiony. */
export const MONITOR_SIMULATED_STATES = ['up', 'down'] as const;
export type MonitorSimulatedState = (typeof MONITOR_SIMULATED_STATES)[number];

/** Bieżący stan monitora (`unknown` = jeszcze bez rozstrzygnięcia: nowy monitor albo po zmianie adresu). */
export const MONITOR_STATES = ['up', 'down', 'unknown'] as const;
export type MonitorState = (typeof MONITOR_STATES)[number];

/** Incydent: otwarty (CHECK: bez `resolved_at`) albo zamknięty (CHECK: `resolved_at` ≥ `opened_at`). */
export const INCIDENT_STATUSES = ['open', 'resolved'] as const;
export type IncidentStatus = (typeof INCIDENT_STATUSES)[number];

export const MONITOR_DEFAULTS = {
  method: 'GET',
  expectedStatus: 200,
  intervalSec: 120,
  timeoutMs: 10_000,
  failThreshold: 2,
  recoverThreshold: 2,
} as const satisfies {
  method: MonitorMethod;
  expectedStatus: number;
  intervalSec: number;
  timeoutMs: number;
  failThreshold: number;
  recoverThreshold: number;
};

export const MONITOR_LIMITS = {
  intervalSec: { min: 60, max: 86_400 },
  timeoutMs: { min: 1_000, max: 60_000 },
  threshold: { min: 1, max: 10 },
  expectedStatus: { min: 100, max: 599 },
  keywordMax: 200,
} as const;

/** Retencja wyników sprawdzeń (DATA_MODEL `monitor_checks`). */
export const MONITOR_CHECK_RETENTION_DAYS = 90;
/** Okno dostępności w karcie zasobu (MODULE_UTRZYMANIE §5: „dostępność 30 dni”). */
export const MONITOR_UPTIME_WINDOW_DAYS = 30;

/** Kolejki workera (CONVENTIONS §8): przebieg „do sprawdzenia” i retencja. */
export const MONITORING_QUEUES = { check: 'monitoring.check', retention: 'monitoring.retention' } as const;

/** Odnośniki zdarzeń (`events.links`). */
export const MONITORING_LINK_TYPES = { monitor: 'monitor', incident: 'incident' } as const;

export const MONITOR_SIMULATION_NOTE = 'Symulacja — strona nie jest naprawdę sprawdzana';

export const monitorFieldsShape = {
  name: nameSchema,
  url: urlSchema,
  method: z.enum(MONITOR_METHODS).default(MONITOR_DEFAULTS.method),
  expectedStatus: z
    .number()
    .int()
    .min(MONITOR_LIMITS.expectedStatus.min)
    .max(MONITOR_LIMITS.expectedStatus.max)
    .default(MONITOR_DEFAULTS.expectedStatus),
  keyword: z.string().trim().min(1).max(MONITOR_LIMITS.keywordMax).nullable().optional(),
  intervalSec: z
    .number()
    .int()
    .min(MONITOR_LIMITS.intervalSec.min, { message: 'Najczęściej co minutę' })
    .max(MONITOR_LIMITS.intervalSec.max)
    .default(MONITOR_DEFAULTS.intervalSec),
  timeoutMs: z
    .number()
    .int()
    .min(MONITOR_LIMITS.timeoutMs.min)
    .max(MONITOR_LIMITS.timeoutMs.max)
    .default(MONITOR_DEFAULTS.timeoutMs),
  failThreshold: z
    .number()
    .int()
    .min(MONITOR_LIMITS.threshold.min)
    .max(MONITOR_LIMITS.threshold.max)
    .default(MONITOR_DEFAULTS.failThreshold),
  recoverThreshold: z
    .number()
    .int()
    .min(MONITOR_LIMITS.threshold.min)
    .max(MONITOR_LIMITS.threshold.max)
    .default(MONITOR_DEFAULTS.recoverThreshold),
  enabled: z.boolean().default(true),
  mode: z.enum(MONITOR_MODES).default('live'),
  simulatedState: z.enum(MONITOR_SIMULATED_STATES).nullable().optional(),
  assetId: idSchema.nullable().optional(),
  clientId: idSchema.nullable().optional(),
};

export const monitorFieldsSchema = z.object(monitorFieldsShape).superRefine((v, ctx) => {
  if (v.mode === 'simulated' && !v.simulatedState) {
    ctx.addIssue({ code: z.ZodIssueCode.custom, path: ['simulatedState'], message: 'Wybierz stan symulacji' });
  }
});
export type MonitorFields = z.infer<typeof monitorFieldsSchema>;

// ── Logika progów (czysta funkcja: worker, testy, UI) ───────────────────────

export interface MonitorCounters {
  state: MonitorState;
  consecutiveFailures: number;
  consecutiveSuccesses: number;
}

export interface MonitorCheckOutcome extends MonitorCounters {
  /** przejście w awarię: otwórz incydent (jeden na serię błędów) */
  opened: boolean;
  /** potwierdzona poprawa: zamknij otwarty incydent */
  resolved: boolean;
}

/**
 * Stan po sprawdzeniu (MVP §14): `fail_threshold` kolejnych niepowodzeń → `down` + `opened` (raz), dalsze błędy
 * niczego nie otwierają; `recover_threshold` kolejnych sukcesów po awarii → `up` + `resolved`. Pojedynczy błąd
 * przy `up` / `unknown` niczego nie zmienia poza licznikiem. Nowy monitor (`unknown`) przechodzi w `up` po pierwszym
 * sukcesie (bez incydentu do zamknięcia).
 */
export function applyMonitorCheck(
  prev: MonitorCounters,
  ok: boolean,
  thresholds: { failThreshold: number; recoverThreshold: number },
): MonitorCheckOutcome {
  if (ok) {
    const successes = prev.consecutiveSuccesses + 1;
    if (prev.state === 'down') {
      const resolved = successes >= thresholds.recoverThreshold;
      return {
        state: resolved ? 'up' : 'down',
        consecutiveFailures: 0,
        consecutiveSuccesses: successes,
        opened: false,
        resolved,
      };
    }
    return { state: 'up', consecutiveFailures: 0, consecutiveSuccesses: successes, opened: false, resolved: false };
  }
  const failures = prev.consecutiveFailures + 1;
  if (prev.state === 'down') {
    return { state: 'down', consecutiveFailures: failures, consecutiveSuccesses: 0, opened: false, resolved: false };
  }
  const opened = failures >= thresholds.failThreshold;
  return {
    state: opened ? 'down' : prev.state,
    consecutiveFailures: failures,
    consecutiveSuccesses: 0,
    opened,
    resolved: false,
  };
}

/** Wynik pojedynczego sprawdzenia: status HTTP zgodny z oczekiwanym i (opcjonalnie) słowo kluczowe w treści. */
export function evaluateMonitorResponse(
  response: { statusCode: number | null; body?: string | null; error?: string | null },
  expect: { expectedStatus: number; keyword?: string | null },
): { ok: boolean; error: string | null } {
  if (response.error) return { ok: false, error: response.error };
  if (response.statusCode === null) return { ok: false, error: 'Brak odpowiedzi' };
  if (response.statusCode !== expect.expectedStatus) {
    return { ok: false, error: `HTTP ${response.statusCode} (oczekiwano ${expect.expectedStatus})` };
  }
  if (expect.keyword && !(response.body ?? '').includes(expect.keyword)) {
    return { ok: false, error: `Brak tekstu „${expect.keyword}” na stronie` };
  }
  return { ok: true, error: null };
}

/** Tytuł incydentu i zdarzenia (Figma 02: „rea.pl nie odpowiada”). */
export function incidentTitle(monitorName: string): string {
  return `${monitorName} nie odpowiada`;
}

export function incidentResolvedTitle(monitorName: string): string {
  return `${monitorName} znowu działa`;
}

/**
 * Przegląd Fali 10 — jedna awaria zasobu = jeden alarm: awaria kolejnego monitora tego samego zasobu (np. port serwera
 * przy braku meldunku) dołącza do otwartej awarii — zdarzenie Zwykłe bez push, z tym powodem.
 */
export function incidentJoinedReason(primaryTitle: string): string {
  return `Dołączone do otwartej awarii zasobu: ${primaryTitle}`.slice(0, 500);
}

export function incidentJoinedNote(primaryTitle: string): string {
  return `Ten zasób ma już otwartą awarię („${primaryTitle}”) — bez osobnego powiadomienia.`;
}

/** Poprawa jednego monitoru, gdy awaria zasobu trwa na innych (zdarzenie Do wiadomości). */
export function incidentStillOpenNote(openCount: number): string {
  return openCount === 1
    ? 'Inny monitor tego zasobu nadal ma awarię.'
    : `Inne monitory tego zasobu nadal mają awarię (${openCount}).`;
}

/** Klucze deduplikacji zdarzeń incydentu (otwarcie i zamknięcie — po jednym na incydent). */
export function incidentEventDedupKey(incidentId: string, kind: 'open' | 'resolve'): string {
  return `incident:${incidentId}:${kind}`;
}

/**
 * Dostępność w procentach z liczby sprawdzeń (zaokrąglenie w dół, żeby 1 błąd na 10 000 nie dawał „100”); brak
 * sprawdzeń = null. Domyślnie 1 miejsce po przecinku; karta zasobu (Figma 08: „99,96%”) używa 2.
 */
export function uptimePercent(checks: { total: number; ok: number }, decimals: 1 | 2 = 1): string | null {
  if (checks.total <= 0) return null;
  const scale = 10 ** (decimals + 2);
  // całkowite mnożenie przed dzieleniem: bez błędów zmiennoprzecinkowych przy floor
  const pct = Math.floor((checks.ok * scale) / checks.total) / 10 ** decimals;
  return pct.toFixed(decimals);
}

// ── Adres monitora i ochrona SSRF (czyste funkcje: usługa, UI, checker HTTP) ─

/**
 * Parametry sprawdzenia HTTP (checker `@fos/integrations/http-check`): tylko porty 80 i 443 (konfigurowalne w workerze),
 * najwyżej 3 przekierowania (każde sprawdzane jak pierwszy adres), limit treści 256 KB, własny User-Agent.
 */
export const MONITOR_HTTP = {
  allowedPorts: [80, 443],
  maxRedirects: 3,
  maxBodyBytes: 256 * 1024,
  defaultTimeoutMs: MONITOR_DEFAULTS.timeoutMs,
  userAgent: 'FravityOS-Monitor/1.0 (+https://fravity.pl)',
} as const;

/**
 * Klasa adresu IP: tylko `public` wolno odpytywać. `metadata` = adres usług metadanych chmury (169.254.169.254,
 * fd00:ec2::254), `reserved` = adresy specjalne (dokumentacja, multicast, „ta sieć”, spoza 2000::/3).
 */
export type IpAddressClass = 'public' | 'loopback' | 'private' | 'link_local' | 'metadata' | 'reserved';

function parseIpv4(s: string): number[] | null {
  const parts = s.split('.');
  if (parts.length !== 4) return null;
  const out: number[] = [];
  for (const p of parts) {
    if (!/^\d{1,3}$/.test(p)) return null;
    const n = Number(p);
    if (n > 255) return null;
    out.push(n);
  }
  return out;
}

/** IPv6 → 8 grup 16-bitowych (także `::`, osadzony IPv4, strefa `%eth0` i nawiasy z URL). */
function parseIpv6(input: string): number[] | null {
  let s = input.startsWith('[') && input.endsWith(']') ? input.slice(1, -1) : input;
  const zone = s.indexOf('%');
  if (zone >= 0) s = s.slice(0, zone);
  if (!s.includes(':')) return null;
  const lastColon = s.lastIndexOf(':');
  const tail = s.slice(lastColon + 1);
  if (tail.includes('.')) {
    const v4 = parseIpv4(tail);
    if (!v4) return null;
    const [a = 0, b = 0, c = 0, d = 0] = v4;
    s = `${s.slice(0, lastColon + 1)}${((a << 8) | b).toString(16)}:${((c << 8) | d).toString(16)}`;
  }
  const halves = s.split('::');
  if (halves.length > 2) return null;
  const toGroups = (part: string | undefined) =>
    part ? part.split(':').map((h) => (/^[0-9a-f]{1,4}$/i.test(h) ? Number.parseInt(h, 16) : Number.NaN)) : [];
  const head = toGroups(halves[0]);
  const rest = halves.length === 2 ? toGroups(halves[1]) : [];
  if ([...head, ...rest].some(Number.isNaN)) return null;
  if (halves.length === 1) return head.length === 8 ? head : null;
  const fill = 8 - head.length - rest.length;
  if (fill < 1) return null;
  return [...head, ...new Array<number>(fill).fill(0), ...rest];
}

function classifyIpv4([a = 0, b = 0, c = 0, d = 0]: number[]): IpAddressClass {
  if (a === 169 && b === 254 && c === 169 && d === 254) return 'metadata';
  if (a === 127) return 'loopback';
  if (a === 10) return 'private';
  if (a === 172 && b >= 16 && b <= 31) return 'private';
  if (a === 192 && b === 168) return 'private';
  // CGNAT 100.64.0.0/10 (także metadane Alibaba 100.100.100.200)
  if (a === 100 && b >= 64 && b <= 127) return 'private';
  if (a === 169 && b === 254) return 'link_local';
  if (a === 0) return 'reserved';
  // 192.0.0.0/24 (m.in. metadane Oracle 192.0.0.192), 192.0.2.0/24, 192.88.99.0/24, 198.18.0.0/15,
  // 198.51.100.0/24, 203.0.113.0/24, multicast i zarezerwowane (224.0.0.0 i wyżej)
  if (a === 192 && b === 0 && (c === 0 || c === 2)) return 'reserved';
  if (a === 192 && b === 88 && c === 99) return 'reserved';
  if (a === 198 && (b === 18 || b === 19)) return 'reserved';
  if (a === 198 && b === 51 && c === 100) return 'reserved';
  if (a === 203 && b === 0 && c === 113) return 'reserved';
  if (a >= 224) return 'reserved';
  return 'public';
}

function classifyIpv6(g: number[]): IpAddressClass {
  const [g0 = 0, g1 = 0, g2 = 0, g3 = 0, g4 = 0, g5 = 0, g6 = 0, g7 = 0] = g;
  const v4 = (hi: number, lo: number) => classifyIpv4([hi >> 8, hi & 0xff, lo >> 8, lo & 0xff]);
  const zeroUpTo = (n: number) => g.slice(0, n).every((x) => x === 0);
  // ::ffff:a.b.c.d (IPv4 w IPv6) — klasa osadzonego IPv4
  if (zeroUpTo(5) && g5 === 0xffff) return v4(g6, g7);
  // ::, ::1 i przestarzałe ::a.b.c.d
  if (zeroUpTo(6)) return g6 === 0 && g7 === 1 ? 'loopback' : 'reserved';
  // NAT64 64:ff9b::/96 — osadzony IPv4; 64:ff9b:1::/48 — lokalne
  if (g0 === 0x64 && g1 === 0xff9b && g2 === 0 && g3 === 0 && g4 === 0 && g5 === 0) return v4(g6, g7);
  if (g0 === 0x64 && g1 === 0xff9b && g2 === 1) return 'private';
  // 6to4 2002::/16 — IPv4 w bitach 16–47
  if (g0 === 0x2002) return v4(g1, g2);
  if ((g0 & 0xfe00) === 0xfc00) {
    const awsMetadata = g0 === 0xfd00 && g1 === 0x0ec2 && g2 + g3 + g4 + g5 + g6 === 0 && g7 === 0x254;
    return awsMetadata ? 'metadata' : 'private';
  }
  if ((g0 & 0xffc0) === 0xfe80) return 'link_local';
  if ((g0 & 0xffc0) === 0xfec0) return 'private';
  if ((g0 & 0xff00) === 0xff00) return 'reserved';
  // dokumentacja 2001:db8::/32, Teredo 2001::/32, discard 100::/64
  if (g0 === 0x2001 && (g1 === 0x0db8 || g1 === 0)) return 'reserved';
  if (g0 === 0x0100 && g1 === 0 && g2 === 0 && g3 === 0) return 'reserved';
  // poza globalnym unicastem 2000::/3
  if ((g0 & 0xe000) !== 0x2000) return 'reserved';
  return 'public';
}

/** Klasa adresu IP (v4 albo v6, także w nawiasach z URL); `null` = to nie jest adres IP. */
export function classifyIpAddress(ip: string): IpAddressClass | null {
  const v4 = parseIpv4(ip);
  if (v4) return classifyIpv4(v4);
  const v6 = parseIpv6(ip);
  if (v6) return classifyIpv6(v6);
  return null;
}

/** Nazwy hostów sieci lokalnej (bez pytania DNS): jednoczłonowe, `localhost`, `.local`, `.internal` itd. */
export function isInternalHostname(hostname: string): boolean {
  const h = hostname.toLowerCase().replace(/\.$/, '');
  if (!h.includes('.') && !h.includes(':')) return true;
  return h === 'home.arpa' || /\.(localhost|local|internal|intranet|lan|home\.arpa|localdomain)$/.test(h);
}

export const MONITOR_URL_MESSAGES = {
  invalid: 'Nieprawidłowy adres',
  protocol: 'Adres musi zaczynać się od http:// albo https://',
  credentials: 'Adres nie może zawierać loginu ani hasła',
  internal: 'Adres wskazuje sieć wewnętrzną — monitoring sprawdza tylko publiczne strony',
} as const;

export function monitorPortMessage(ports: readonly number[]): string {
  return `Monitoring sprawdza tylko porty ${ports.join(' i ')}`;
}

/**
 * Problem z adresem monitora (walidacja formularza i usługi): protokół, login w adresie, port spoza listy, adres IP
 * albo nazwa sieci wewnętrznej. Domeny sprawdza checker po rozwiązaniu DNS (także po każdym przekierowaniu).
 */
export function monitorUrlProblem(url: string, opts: { allowedPorts?: readonly number[] } = {}): string | null {
  let u: URL;
  try {
    u = new URL(url);
  } catch {
    return MONITOR_URL_MESSAGES.invalid;
  }
  if (u.protocol !== 'http:' && u.protocol !== 'https:') return MONITOR_URL_MESSAGES.protocol;
  if (u.username || u.password) return MONITOR_URL_MESSAGES.credentials;
  const ports = opts.allowedPorts ?? MONITOR_HTTP.allowedPorts;
  const port = u.port ? Number(u.port) : u.protocol === 'https:' ? 443 : 80;
  if (!ports.includes(port)) return monitorPortMessage(ports);
  const ipClass = classifyIpAddress(u.hostname);
  if (ipClass ? ipClass !== 'public' : isInternalHostname(u.hostname)) return MONITOR_URL_MESSAGES.internal;
  return null;
}

/**
 * Adres podpowiadany dla zasobu: adres (jeśli wygląda na domenę albo URL) albo nazwa zasobu; `https://` dla domeny.
 * Karta zasobu („Włącz monitoring”) i „Dodaj zasób” („Monitoruj dostępność strony”) — jedna reguła.
 */
export function suggestMonitorUrl(asset: { address: string | null; name: string }): string | null {
  for (const raw of [asset.address, asset.name]) {
    const v = raw?.trim();
    if (!v || v.includes('@') || /\s/.test(v)) continue;
    if (/^https?:\/\//i.test(v)) return v;
    if (/^[a-z0-9-]+(\.[a-z0-9-]+)+(\/.*)?$/i.test(v)) return `https://${v}`;
  }
  return null;
}

// ── Rodzaje monitorów: strona (HTTP), port (TCP), meldunek serwera (heartbeat) — Fala 10 ─────────────

/**
 * Rodzaj monitora wynika z adresu w `monitors.url` (bez nowej kolumny — DECISIONS Fala 10 „Monitoring serwera”):
 * `http(s)://…` = strona, `tcp://host:port` = port (np. 22, 443), `heartbeat://<id monitora>` = meldunek serwera
 * (serwer sam wywołuje tajny adres `/api/heartbeat/<token>`; brak meldunku przez N min = incydent).
 */
export const MONITOR_KINDS = ['http', 'tcp', 'heartbeat'] as const;
export type MonitorKind = (typeof MONITOR_KINDS)[number];

export function monitorKindOf(url: string): MonitorKind {
  if (/^tcp:\/\//i.test(url)) return 'tcp';
  if (/^heartbeat:\/\//i.test(url)) return 'heartbeat';
  return 'http';
}

/** Nazwa hosta albo adres IP (v4, v6 w nawiasach lub bez) — bez schematu, ścieżki i portu. */
export const tcpHostSchema = z
  .string()
  .trim()
  .toLowerCase()
  .min(1, { message: 'Podaj nazwę serwera albo adres IP' })
  .max(253, { message: 'Nazwa serwera jest za długa' })
  .transform((v) => (v.startsWith('[') && v.endsWith(']') ? v.slice(1, -1) : v))
  .refine((v) => classifyIpAddress(v) !== null || /^[a-z0-9]([a-z0-9-]{0,62}\.)*[a-z0-9-]{1,63}\.?$/.test(v), {
    message: 'Nieprawidłowa nazwa serwera',
  });

export const tcpPortSchema = z
  .number({ invalid_type_error: 'Podaj numer portu' })
  .int({ message: 'Podaj numer portu' })
  .min(1, { message: 'Port od 1 do 65535' })
  .max(65_535, { message: 'Port od 1 do 65535' });

/** `tcp://host:port` (IPv6 w nawiasach). */
export function tcpTargetUrl(host: string, port: number): string {
  return `tcp://${host.includes(':') ? `[${host}]` : host}:${port}`;
}

export function parseTcpTarget(url: string): { host: string; port: number } | null {
  const m = /^tcp:\/\/(\[[^\]]+\]|[^:/]+):(\d{1,5})$/i.exec(url);
  if (!m) return null;
  const host = (m[1] ?? '').replace(/^\[|\]$/g, '');
  const port = Number(m[2]);
  if (!host || !Number.isInteger(port) || port < 1 || port > 65_535) return null;
  return { host, port };
}

/** Cel monitora do pokazania ludziom: adres strony, `host:port` albo „meldunek serwera” (adres `heartbeat://` to id). */
export function monitorTargetLabel(url: string): string {
  const kind = monitorKindOf(url);
  if (kind === 'tcp') {
    const t = parseTcpTarget(url);
    return t ? `${t.host.includes(':') ? `[${t.host}]` : t.host}:${t.port}` : url;
  }
  if (kind === 'heartbeat') return 'meldunek serwera (heartbeat)';
  return url;
}

/**
 * Problem z celem monitora TCP (formularz i usługa): adres IP spoza internetu albo nazwa sieci wewnętrznej (pętla
 * zwrotna, sieci prywatne, metadane chmury) — jak dla HTTP. Domeny sprawdza checker po rozwiązaniu DNS (wszystkie
 * adresy muszą być publiczne, połączenie na sprawdzony adres).
 */
export function tcpTargetProblem(host: string): string | null {
  const ipClass = classifyIpAddress(host);
  if (ipClass ? ipClass !== 'public' : isInternalHostname(host)) return MONITOR_TCP_MESSAGES.internal;
  return null;
}

/** Host monitora portu: poprawna nazwa i nie sieć wewnętrzna (DNS sprawdza checker). */
export const tcpPublicHostSchema = tcpHostSchema.refine((h) => tcpTargetProblem(h) === null, {
  message: 'Adres wskazuje sieć wewnętrzną — monitoring sprawdza tylko publiczne serwery',
});

export const MONITOR_TCP = {
  defaultTimeoutMs: 5_000,
  /** porty, które warto podpowiedzieć (SSH, HTTPS, HTTP, poczta, bazy) */
  suggestedPorts: [22, 443, 80, 21, 25, 465, 587, 993, 3306, 5432] as const,
} as const;

export const MONITOR_TCP_MESSAGES = {
  internal: 'Adres wskazuje sieć wewnętrzną — monitoring sprawdza tylko publiczne serwery',
  refused: 'Odmowa połączenia (port zamknięty)',
  timeout: 'Przekroczono czas połączenia',
  unreachable: 'Serwer jest nieosiągalny',
  dns: 'Nie znaleziono serwera',
  portBlocked: 'Ten port nie jest dozwolony w konfiguracji monitoringu',
  generic: 'Błąd połączenia',
} as const;

export function heartbeatTargetUrl(monitorId: string): string {
  return `heartbeat://${monitorId}`;
}

/** Meldunek serwera (heartbeat): tajny adres, progi i limity. */
export const HEARTBEAT = {
  tokenPrefix: 'fos_hb_',
  /** ścieżka API (token na końcu — ten adres jest sekretem, logi API go redagują) */
  path: '/api/heartbeat/',
  /** brak meldunku dłużej niż N min = błąd (domyślnie 10 min przy cronie co 5 min) */
  maxSilenceMin: { min: 2, max: 7 * 24 * 60, default: 10 },
  /** co ile worker sprawdza ciszę */
  intervalSec: 60,
  failThreshold: 2,
  recoverThreshold: 1,
  /** limit meldunków jednego tokenu na minutę (API) */
  perMinute: 12,
  /** jawna część tokenu w UI (`fos_hb_ab12…`) */
  prefixChars: 4,
} as const;

/** Tylko poprawny kształt tokenu (`fos_hb_` + 43 znaki base64url) — inne adresy od razu 404. */
export const HEARTBEAT_TOKEN_RE = /^fos_hb_[A-Za-z0-9_-]{43}$/;

export function heartbeatTokenPrefix(token: string): string {
  return token.slice(0, HEARTBEAT.tokenPrefix.length + HEARTBEAT.prefixChars);
}

/**
 * Treść meldunku (JSON, opcjonalnie): zajętość dysku w procentach i obciążenie (load average z 1 min). Nieznane pola
 * są pomijane; zła treść = meldunek bez metryk (sam meldunek się liczy).
 */
export const heartbeatPayloadSchema = z.object({
  disk_used_percent: z.coerce.number().min(0).max(100).optional(),
  load1: z.coerce.number().min(0).max(100_000).optional(),
});
export type HeartbeatPayload = z.infer<typeof heartbeatPayloadSchema>;

export interface HeartbeatMetrics {
  diskUsedPercent: number | null;
  load1: number | null;
}

export function heartbeatMetricsOf(payload: unknown): HeartbeatMetrics {
  const obj = payload && typeof payload === 'object' ? (payload as Record<string, unknown>) : {};
  // każde pole osobno: zła wartość jednego (np. dysk spoza 0–100) nie kasuje drugiego
  const field = <K extends keyof HeartbeatPayload>(key: K): number | null => {
    const raw = obj[key];
    if (raw === null || raw === undefined || (typeof raw === 'string' && raw.trim() === '')) return null;
    const parsed = heartbeatPayloadSchema.shape[key].safeParse(raw);
    return parsed.success && parsed.data !== undefined ? parsed.data : null;
  };
  return { diskUsedPercent: field('disk_used_percent'), load1: field('load1') };
}

function plNumber(n: number): string {
  return (Math.round(n * 100) / 100).toString().replace('.', ',');
}

export const HEARTBEAT_MESSAGES = {
  silent: (min: number) => `Brak meldunku od ${min} min`,
  never: 'Serwer jeszcze się nie zameldował',
  disk: (p: number, max: number) => `Mało miejsca na dysku: zajęte ${plNumber(p)}% (próg ${max}%)`,
  load: (v: number, max: number) => `Wysokie obciążenie: ${plNumber(v)} (próg ${plNumber(max)})`,
} as const;

/** Wynik meldunku z metrykami: przekroczony próg = nieudane sprawdzenie (incydent po progu błędów). */
export function evaluateHeartbeatMetrics(
  m: HeartbeatMetrics,
  t: { diskMaxPercent: number | null; load1Max: number | null },
): { ok: boolean; error: string | null } {
  if (t.diskMaxPercent !== null && m.diskUsedPercent !== null && m.diskUsedPercent >= t.diskMaxPercent) {
    return { ok: false, error: HEARTBEAT_MESSAGES.disk(m.diskUsedPercent, t.diskMaxPercent) };
  }
  if (t.load1Max !== null && m.load1 !== null && m.load1 >= t.load1Max) {
    return { ok: false, error: HEARTBEAT_MESSAGES.load(m.load1, t.load1Max) };
  }
  return { ok: true, error: null };
}

/** Cisza: `null` = meldunek świeży; inaczej błąd sprawdzenia. */
export function heartbeatSilenceError(
  v: { lastBeatAt: Date | null; since: Date; maxSilenceSec: number },
  now: Date,
): string | null {
  const from = v.lastBeatAt ?? v.since;
  const silentSec = Math.floor((now.getTime() - from.getTime()) / 1000);
  if (silentSec <= v.maxSilenceSec) return null;
  return v.lastBeatAt ? HEARTBEAT_MESSAGES.silent(Math.floor(silentSec / 60)) : HEARTBEAT_MESSAGES.never;
}

/** Adres meldunku (pokazywany raz, przy utworzeniu i rotacji tokenu). */
export function heartbeatUrl(origin: string, token: string): string {
  return `${origin.replace(/\/+$/, '')}${HEARTBEAT.path}${token}`;
}

/**
 * Linia crona (co 5 min) do skopiowania: meldunek z zajętością dysku `/` i obciążeniem; bez znaku `%` (w crontab to
 * nowa linia). `simple` = sam meldunek bez metryk.
 */
export function heartbeatCronLine(url: string, opts: { simple?: boolean } = {}): string {
  const base = 'curl -fsS -m 10 --retry 2 -o /dev/null';
  if (opts.simple) return `*/5 * * * * ${base} ${url}`;
  const body =
    // kolumna „Capacity” = przedostatnia (nazwa systemu plików bywa ze spacjami)
    '"{\\"disk_used_percent\\":$(df -P / | awk \'NR==2{print $(NF-1)+0}\'),\\"load1\\":$(cut -d\' \' -f1 /proc/loadavg)}"';
  return `*/5 * * * * ${base} -X POST -H 'Content-Type: application/json' -d ${body} ${url}`;
}

/** Tytuł incydentu wg rodzaju monitora i przyczyny (strona i port: „… nie odpowiada”). */
export function incidentTitleFor(kind: MonitorKind, monitorName: string, cause: string | null): string {
  if (kind === 'heartbeat' && cause) {
    if (cause.startsWith('Mało miejsca na dysku')) return `${monitorName}: mało miejsca na dysku`;
    if (cause.startsWith('Wysokie obciążenie')) return `${monitorName}: wysokie obciążenie`;
    return `${monitorName} nie melduje się`;
  }
  return incidentTitle(monitorName);
}

/** Podmiot w treści zdarzenia: „Strona rea.pl”, „Serwer prod · port 22”, „Serwer prod · meldunek”. */
export function monitorSubject(kind: MonitorKind, monitorName: string): string {
  return kind === 'http' ? `Strona ${monitorName}` : `Serwer ${monitorName}`;
}

// ── Symulacja (tryb `simulated`) ────────────────────────────────────────────

/** Odpowiedź strony (checker HTTP albo symulacja) — wejście `evaluateMonitorResponse`. */
export interface MonitorResponse {
  statusCode: number | null;
  body?: string | null;
  error?: string | null;
  latencyMs?: number | null;
}

export const MONITOR_SIMULATED_ERROR = 'Przekroczono czas odpowiedzi (symulacja)';
export const MONITOR_SIMULATED_LATENCY_MS = 180;

/** Wynik symulacji: „działa” = oczekiwany status (i słowo kluczowe), „nie działa” = przekroczony czas. */
export function simulatedMonitorResponse(
  state: MonitorSimulatedState | null,
  expect: { expectedStatus: number; keyword?: string | null },
): MonitorResponse {
  if (state === 'down') return { statusCode: null, body: null, error: MONITOR_SIMULATED_ERROR, latencyMs: null };
  return {
    statusCode: expect.expectedStatus,
    body: expect.keyword ?? '',
    error: null,
    latencyMs: MONITOR_SIMULATED_LATENCY_MS,
  };
}

// ── Zdarzenia ───────────────────────────────────────────────────────────────

/**
 * Typy zdarzeń monitoringu: alarm (Pilne, push) i powrót (Zwykłe) incydentu oraz pilne zgłoszenie klienta mailem
 * (MVP §14: „monitoring i reguły decydują o potwierdzonej awarii” — zgłoszenie bez awarii w monitoringu = zdarzenie
 * Pilne ze źródłem „zgłoszenie klienta”, nie incydent).
 */
export const MONITORING_EVENT_TYPES = {
  down: EVENT_TYPES.monitorDown,
  up: EVENT_TYPES.monitorUp,
  clientReport: 'monitor.client_report',
} as const;

export const CLIENT_REPORT_SENDER = 'Zgłoszenie klienta';

/** Jedno zgłoszenie na wiadomość (ponowiona klasyfikacja niczego nie dubluje). */
export function clientReportDedupKey(messageId: string): string {
  return `client-report:${messageId}`;
}

/** Okno, w którym wcześniejsze zgłoszenia klienta dołączamy do nowo otwartego incydentu. */
export const CLIENT_REPORT_LINK_WINDOW_HOURS = 6;

// ── Wejścia usług (router `monitoring`) ─────────────────────────────────────

export const MONITOR_KEYWORD_NEEDS_GET = 'Słowo kluczowe wymaga metody GET';

function refineUrl(
  v: {
    url?: string | undefined;
    method?: MonitorMethod | undefined;
    keyword?: string | null | undefined;
  },
  ctx: z.RefinementCtx,
) {
  if (v.method === 'HEAD' && v.keyword) {
    ctx.addIssue({ code: z.ZodIssueCode.custom, path: ['keyword'], message: MONITOR_KEYWORD_NEEDS_GET });
  }
  if (!v.url) return;
  const problem = monitorUrlProblem(v.url);
  if (problem) ctx.addIssue({ code: z.ZodIssueCode.custom, path: ['url'], message: problem });
}

const f = monitorFieldsShape;

export const monitorRefInput = z.object({ id: idSchema }).strict();
export const assetMonitoringInput = z.object({ assetId: idSchema }).strict();

/**
 * Nowy monitor zasobu („Włącz monitoring” w karcie zasobu). Klient = klient zasobu. Tryb domyślnie z integracji
 * monitoringu workspace'u (`http` połączona = live, inaczej symulacja); inny tryb wybiera tylko owner/admin.
 */
export const createMonitorInput = z
  .object({
    assetId: idSchema,
    name: nameSchema.optional(),
    url: urlSchema,
    method: f.method,
    expectedStatus: f.expectedStatus,
    keyword: f.keyword,
    intervalSec: f.intervalSec,
    timeoutMs: f.timeoutMs,
    failThreshold: f.failThreshold,
    recoverThreshold: f.recoverThreshold,
    enabled: f.enabled,
    mode: z.enum(MONITOR_MODES).optional(),
  })
  .strict()
  .superRefine(refineUrl);
export type CreateMonitorInput = z.input<typeof createMonitorInput>;

/**
 * Edycja monitora. Zamiast `version` (worker zmienia wiersz przy każdym sprawdzeniu) — `configHash` z `MonitorDto`:
 * inna konfiguracja w bazie = CONFLICT. Zmiana trybu: owner/admin.
 */
export const updateMonitorInput = z
  .object({
    id: idSchema,
    configHash: z.string().min(1).max(128),
    name: nameSchema.optional(),
    url: urlSchema.optional(),
    /** monitor portu (TCP, Fala 10): nowy cel; strona zmienia `url` */
    host: tcpPublicHostSchema.optional(),
    port: tcpPortSchema.optional(),
    method: f.method.removeDefault().optional(),
    expectedStatus: f.expectedStatus.removeDefault().optional(),
    keyword: f.keyword,
    intervalSec: f.intervalSec.removeDefault().optional(),
    timeoutMs: f.timeoutMs.removeDefault().optional(),
    failThreshold: f.failThreshold.removeDefault().optional(),
    recoverThreshold: f.recoverThreshold.removeDefault().optional(),
    enabled: z.boolean().optional(),
    mode: z.enum(MONITOR_MODES).optional(),
  })
  .strict()
  .superRefine(refineUrl);
export type UpdateMonitorInput = z.input<typeof updateMonitorInput>;

/** „Symuluj awarię” / „Przywróć” (owner/admin, tylko monitor w trybie symulacji). */
export const setMonitorSimulationInput = z.object({ id: idSchema, state: z.enum(MONITOR_SIMULATED_STATES) }).strict();

export const listMonitorsInput = z
  .object({
    assetId: idSchema.optional(),
    clientId: idSchema.optional(),
    includeArchived: z.boolean().default(false),
  })
  .strict()
  .default({});

export const listIncidentsInput = z
  .object({
    assetId: idSchema.optional(),
    clientId: idSchema.optional(),
    monitorId: idSchema.optional(),
    status: z.enum(INCIDENT_STATUSES).optional(),
    limit: z.number().int().min(1).max(100).default(20),
  })
  .strict()
  .default({});

export const openIncidentsInput = z
  .object({ limit: z.number().int().min(1).max(50).default(5) })
  .strict()
  .default({});

// ── Monitoring serwera (Fala 10): porty TCP i meldunek (heartbeat) ─────────

/** Najwyżej tyle monitorów (bez archiwum) na jeden serwer. */
export const SERVER_MONITORS_MAX = 10;

const heartbeatThresholdsShape = {
  maxSilenceMin: z
    .number()
    .int()
    .min(HEARTBEAT.maxSilenceMin.min, { message: `Co najmniej ${HEARTBEAT.maxSilenceMin.min} min` })
    .max(HEARTBEAT.maxSilenceMin.max, { message: 'Najwyżej 7 dni' }),
  diskMaxPercent: z.number().int().min(1).max(100).nullable(),
  load1Max: z.number().min(0.1).max(10_000).nullable(),
};

/**
 * Nowy monitor serwera (zasób typu „Serwer” w Utrzymaniu; kilka monitorów na serwer): port TCP (np. 22 / 443, tryb
 * z integracji monitoringu jak strona) albo meldunek (heartbeat — zawsze „na żywo”: to serwer się melduje).
 */
export const createServerMonitorInput = z.discriminatedUnion('kind', [
  z
    .object({
      kind: z.literal('tcp'),
      assetId: idSchema,
      name: nameSchema.optional(),
      host: tcpPublicHostSchema,
      port: tcpPortSchema,
      intervalSec: f.intervalSec,
      timeoutMs: f.timeoutMs.removeDefault().default(MONITOR_TCP.defaultTimeoutMs),
      failThreshold: f.failThreshold,
      recoverThreshold: f.recoverThreshold,
      mode: z.enum(MONITOR_MODES).optional(),
    })
    .strict(),
  z
    .object({
      kind: z.literal('heartbeat'),
      assetId: idSchema,
      name: nameSchema.optional(),
      maxSilenceMin: heartbeatThresholdsShape.maxSilenceMin.default(HEARTBEAT.maxSilenceMin.default),
      diskMaxPercent: heartbeatThresholdsShape.diskMaxPercent.default(90),
      load1Max: heartbeatThresholdsShape.load1Max.default(null),
      failThreshold: f.failThreshold.removeDefault().default(HEARTBEAT.failThreshold),
      recoverThreshold: f.recoverThreshold.removeDefault().default(HEARTBEAT.recoverThreshold),
    })
    .strict(),
]);
export type CreateServerMonitorInput = z.input<typeof createServerMonitorInput>;

export const updateHeartbeatSettingsInput = z
  .object({
    id: idSchema,
    maxSilenceMin: heartbeatThresholdsShape.maxSilenceMin.optional(),
    diskMaxPercent: heartbeatThresholdsShape.diskMaxPercent.optional(),
    load1Max: heartbeatThresholdsShape.load1Max.optional(),
  })
  .strict();
export type UpdateHeartbeatSettingsInput = z.input<typeof updateHeartbeatSettingsInput>;

export const MONITORING_MESSAGES = {
  assetHasMonitor: 'Ten zasób ma już monitor',
  notServer: 'Porty i meldunki dodaje się do zasobu typu „Serwer”',
  serverMonitorsLimit: `Serwer ma już ${SERVER_MONITORS_MAX} monitorów`,
  notHeartbeat: 'To nie jest monitor meldunku serwera',
  notTcp: 'Serwer i port zmienia się tylko w monitorze portu',
  kindChange: 'Rodzaju monitora nie można zmienić — dodaj nowy monitor',
  heartbeatInvalid: 'Nieznany adres meldunku',
  configChanged: 'Ktoś zmienił ustawienia monitora. Zamknij okno i spróbuj ponownie.',
  simulationAdminsOnly: 'Symulację przełącza tylko właściciel albo administrator',
  simulationDevOnly: 'Symulacja awarii działa tylko poza produkcją',
  modeAdminsOnly: 'Tryb sprawdzania zmienia tylko właściciel albo administrator',
  notSimulated: 'Ten monitor sprawdza prawdziwą stronę — symulacja jest wyłączona',
  archived: 'Monitor jest wyłączony z użycia',
  assetArchived: 'Zasób jest w archiwum',
  notIncoming: 'Zgłoszeniem może być tylko wiadomość przychodząca',
} as const;

// ── DTO ─────────────────────────────────────────────────────────────────────

export interface MonitorDto {
  id: string;
  name: string;
  /** Fala 10: strona (HTTP), port (TCP) albo meldunek serwera */
  kind: MonitorKind;
  /** monitor portu: serwer i port; inne rodzaje — null */
  target: { host: string; port: number } | null;
  url: string;
  method: MonitorMethod;
  expectedStatus: number;
  keyword: string | null;
  intervalSec: number;
  timeoutMs: number;
  failThreshold: number;
  recoverThreshold: number;
  enabled: boolean;
  mode: MonitorMode;
  simulatedState: MonitorSimulatedState | null;
  state: MonitorState;
  stateChangedAt: string | null;
  lastCheckedAt: string | null;
  nextCheckAt: string;
  lastError: string | null;
  assetId: string | null;
  clientId: string | null;
  /** skrót konfiguracji do edycji współbieżnej (`updateMonitorInput.configHash`) */
  configHash: string;
  archivedAt: string | null;
}

export interface IncidentDto {
  id: string;
  monitorId: string;
  assetId: string | null;
  clientId: string | null;
  status: IncidentStatus;
  title: string;
  cause: string | null;
  openedAt: string;
  resolvedAt: string | null;
  /** czas trwania: do zamknięcia albo do teraz (otwarty) */
  durationSec: number;
  simulated: boolean;
  openEventId: string | null;
  resolveEventId: string | null;
}

export interface MonitorCheckDto {
  id: string;
  checkedAt: string;
  ok: boolean;
  statusCode: number | null;
  latencyMs: number | null;
  error: string | null;
  simulated: boolean;
}

/** Dzień paska dostępności (Europe/Warsaw); `percent` = null, gdy tego dnia nie było sprawdzeń. */
export interface UptimeDay {
  date: string;
  total: number;
  ok: number;
  percent: string | null;
}

export interface MonitorUptime {
  windowDays: number;
  total: number;
  ok: number;
  /** 2 miejsca po przecinku („99.96”) */
  percent: string | null;
  days: UptimeDay[];
}

/** Sekcja Monitoring w karcie zasobu (Figma 08: „Dostępność 30 dni”, „Ostatni problem”). */
export interface AssetMonitoringDto {
  assetId: string;
  monitor: MonitorDto | null;
  uptime: MonitorUptime | null;
  lastCheck: MonitorCheckDto | null;
  openIncident: IncidentDto | null;
  /** ostatni zamknięty incydent */
  lastProblem: IncidentDto | null;
  /** ostatnie incydenty (najnowsze pierwsze, do 5) */
  incidents: IncidentDto[];
  /** moduł Utrzymanie = Edycja i zakres z edycją zasobu */
  canEdit: boolean;
  /** owner/admin: przełącznik symulacji i trybu sprawdzania */
  canManageMode: boolean;
  /** „Symuluj awarię / Przywróć” (narzędzie dev): owner/admin i tylko poza produkcją (Fala 6) */
  canSimulate: boolean;
  /** tryb nowego monitora (integracja monitoringu workspace'u) */
  defaultMode: MonitorMode;
  /** adres podpowiadany przy „Włącz monitoring” (z adresu albo nazwy zasobu) */
  suggestedUrl: string | null;
}

/** Odcinek osi czasu incydentu: kolejne sprawdzenia z tym samym wynikiem. */
export interface IncidentCheckSegment {
  ok: boolean;
  from: string;
  to: string;
  count: number;
  error: string | null;
  statusCode: number | null;
  simulated: boolean;
}

export interface IncidentMessageDto {
  id: string;
  threadId: string;
  subject: string;
  fromEmail: string;
  fromName: string | null;
  snippet: string;
  sentAt: string;
}

/** Widok incydentu `/incydenty/:id` (czeka na projekt w Figmie). */
export interface IncidentDetail {
  incident: IncidentDto;
  monitor: Pick<
    MonitorDto,
    'id' | 'name' | 'url' | 'mode' | 'state' | 'intervalSec' | 'failThreshold' | 'recoverThreshold'
  >;
  asset: { id: string; name: string } | null;
  client: { id: string; name: string; color: string | null } | null;
  /** od ostatniego sukcesu przed awarią do zamknięcia (albo teraz) */
  timeline: IncidentCheckSegment[];
  checks: { total: number; failed: number };
  /** powiązane wiadomości (zgłoszenia klienta); bez modułu Inbox — puste i `messagesHidden` */
  messages: IncidentMessageDto[];
  messagesHidden: boolean;
  canEdit: boolean;
}

/** Otwarty incydent do kompozycji (Dziś „Alarmy”, kafelek Utrzymania). */
export interface OpenIncidentItem {
  id: string;
  title: string;
  openedAt: string;
  durationSec: number;
  assetId: string | null;
  assetName: string | null;
  clientId: string | null;
  clientName: string | null;
  simulated: boolean;
  /** ścieżka SPA: `/incydenty/:id` */
  to: string;
}

export interface OpenIncidents {
  count: number;
  items: OpenIncidentItem[];
}

/** Meldunek serwera: ustawienia i ostatni meldunek (bez tokenu — ten jest pokazywany tylko raz). */
export interface HeartbeatInfoDto {
  /** jawny początek tokenu (`fos_hb_ab12`) — do rozpoznania, który adres jest w cronie */
  tokenPrefix: string;
  tokenRotatedAt: string;
  maxSilenceMin: number;
  diskMaxPercent: number | null;
  load1Max: number | null;
  lastBeatAt: string | null;
  lastMetrics: HeartbeatMetrics | null;
}

export interface ServerMonitorItem {
  monitor: MonitorDto;
  heartbeat: HeartbeatInfoDto | null;
  lastCheck: MonitorCheckDto | null;
  openIncident: IncidentDto | null;
  /** dostępność 30 dni (2 miejsca po przecinku) */
  uptimePercent: string | null;
}

/** Sekcja „Monitoring serwera” w karcie zasobu typu Serwer (czeka na projekt w Figmie). */
export interface ServerMonitoringDto {
  assetId: string;
  items: ServerMonitorItem[];
  /** ostatnie incydenty serwera (wszystkie monitory, najnowsze pierwsze, do 5) */
  incidents: IncidentDto[];
  canEdit: boolean;
  canManageMode: boolean;
  canSimulate: boolean;
  defaultMode: MonitorMode;
  /** host podpowiadany przy „Dodaj port” (z adresu albo nazwy zasobu) */
  suggestedHost: string | null;
}

/** Token meldunku — pokazany RAZ (utworzenie monitora, rotacja); adres i linię crona składa UI z adresem aplikacji. */
export interface HeartbeatTokenDto {
  monitor: MonitorDto;
  token: string;
}

/** Host podpowiadany dla serwera: adres (domena / IP, także z adresu URL) albo nazwa zasobu. */
export function suggestServerHost(asset: { address: string | null; name: string }): string | null {
  for (const raw of [asset.address, asset.name]) {
    let v = raw?.trim().toLowerCase();
    if (!v || v.includes('@') || /\s/.test(v)) continue;
    if (/^[a-z]+:\/\//.test(v)) {
      try {
        v = new URL(v).hostname;
      } catch {
        continue;
      }
    }
    if (classifyIpAddress(v) !== null || /^[a-z0-9-]+(\.[a-z0-9-]+)+$/.test(v)) return v;
  }
  return null;
}

/** Ścieżka SPA incydentu. */
export function incidentPath(id: string): string {
  return `/incydenty/${id}`;
}

/** Wynik obsługi pilnego zgłoszenia mailem (`handleUrgentReport` w module monitoring). */
export interface UrgentReportOutcome {
  /** `incident_linked` = wiadomość dołączona do otwartego incydentu klienta (bez nowego alarmu i push) */
  outcome: 'incident_linked' | 'report_created';
  incidentId: string | null;
  eventId: string;
  deduplicated: boolean;
}
